/**
 * Database Maintenance Helper
 * 
 * Responsible for database cleanup, termination of connections,
 * and other maintenance tasks.
 */

import { DB_CONSTANTS } from './dbStorageHelper';

class DbMaintenance {
    // Add helper methods to terminate background operations and force close connections
    terminateBackgroundOperations() {
        // console.log('[dbStorageService] Terminating background operations');
        
        // Set a flag to indicate we're in cleanup mode
        window.dbCleanupInProgress = true;
        
        // Dispatch a custom event to notify other services to release database connections
        const terminateEvent = new CustomEvent('database-cleanup-requested', {
            detail: { dbName: DB_CONSTANTS.DB_NAME }
        });
        window.dispatchEvent(terminateEvent);
        
        // Check for known services that might have open connections and try to terminate them
        try {
            // Try to terminate notification service intervals if it exists
            if (window.notificationCheckInterval) {
                // console.log('[dbStorageService] Clearing notification check interval');
                clearInterval(window.notificationCheckInterval);
                window.notificationCheckInterval = null;
            }
            
            // Additional services can be terminated here
            // ...
            
        } catch (error) {
            console.warn('[dbStorageService] Error terminating background operations:', error);
        }
    }
    
    /**
     * Check if the database is currently in the process of being cleaned up
     * Can be used by components to avoid accessing the database during logout
     * @returns {boolean} Whether cleanup is in progress
     */
    isCleanupInProgress() {
        return window.dbCleanupInProgress === true;
    }

    cleanup(db) {
        // First terminate any background operations that might be using the database
        this.terminateBackgroundOperations();
        
        return new Promise((resolve, reject) => {
            // Set a timeout to prevent hanging indefinitely
            const timeoutId = setTimeout(() => {
                console.error(`[dbStorageService] Database cleanup timed out after 10 seconds`);
                // Even if cleanup times out, reset the cleanup flag
                window.dbCleanupInProgress = false;
                reject(new Error('Database cleanup timed out after 10 seconds'));
            }, 10000);
            
            try {
                // First check if we have a database connection
                if (db) {
                    // console.log('[dbStorageService] Closing existing database connection');
                    
                    // Try to close the connection properly
                    try {
                        db.close();
                    } catch (closeError) {
                        console.warn('[dbStorageService] Error closing database:', closeError);
                        // Continue with deletion attempt even if close fails
                    }
                }
                
                // Force garbage collection if possible to clean up lingering connections
                if (typeof window.gc === 'function') {
                    try {
                        window.gc();
                        // console.log('[dbStorageService] Forced garbage collection');
                    } catch (gcError) {
                        console.warn('[dbStorageService] Unable to force garbage collection:', gcError);
                    }
                }
                
                // Create the delete request
                const request = indexedDB.deleteDatabase(DB_CONSTANTS.DB_NAME);
                
                // Handle blocked event - very important!
                request.onblocked = (event) => {
                    console.warn(`[dbStorageService] Database deletion blocked. Waiting for connections to close...`);
                    
                    // Additional attempt to force close remaining connections
                    this.forceCloseOtherConnections();
                    
                    // The promise stays pending until connections are closed
                    // or until our timeout fires
                };
                
                request.onsuccess = () => {
                    clearTimeout(timeoutId);
                    // console.log(`[dbStorageService] Database '${DB_CONSTANTS.DB_NAME}' successfully deleted`);
                    // Reset cleanup flag after successful deletion
                    window.dbCleanupInProgress = false;
                    resolve(true);
                };
                
                request.onerror = (event) => {
                    clearTimeout(timeoutId);
                    console.error(`[dbStorageService] Error deleting database:`, event.target.error);
                    // Reset cleanup flag even on error
                    window.dbCleanupInProgress = false;
                    reject(event.target.error);
                };
            } catch (error) {
                clearTimeout(timeoutId);
                console.error('[dbStorageService] Unexpected error during cleanup:', error);
                // Reset cleanup flag on any error
                window.dbCleanupInProgress = false;
                reject(error);
            }
        });
    }

    /**
     * Forces closure of other connections to the database
     * Called when database deletion is blocked by existing connections
     */
    forceCloseOtherConnections() {
        // console.log('[dbStorageService] Attempting to force close other database connections');
        
        // Dispatch another more forceful cleanup event
        const forceCloseEvent = new CustomEvent('database-force-close', {
            detail: { dbName: DB_CONSTANTS.DB_NAME }
        });
        window.dispatchEvent(forceCloseEvent);
        
        // Try to trigger garbage collection to release object references
        if (typeof window.gc === 'function') {
            try {
                window.gc();
            } catch (error) {
                console.warn('[dbStorageService] Error forcing garbage collection:', error);
            }
        }
        
        // Additional techniques to release connections
        try {
            // Try to access and close connections from IDB factory's open connections list
            // This is a non-standard approach but sometimes works
            if (window.indexedDB && window.indexedDB.databases) {
                window.indexedDB.databases()
                    .then(databases => {
                        databases.forEach(database => {
                            if (database.name === DB_CONSTANTS.DB_NAME) {
                                // console.log(`[dbStorageService] Found database in list, attempting to close`);
                                indexedDB.deleteDatabase(database.name);
                            }
                        });
                    })
                    .catch(error => {
                        console.warn('[dbStorageService] Error accessing databases list:', error);
                    });
            }
        } catch (error) {
            console.warn('[dbStorageService] Error in forceCloseOtherConnections:', error);
        }
    }
}

export default new DbMaintenance(); 